import React, { useEffect, useState } from "react";
import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  DocumentCheckIcon,
  CheckIcon,
  ArrowPathIcon,
  UserIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AdjustmentsVerticalIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import AddProject from "./AddProject";
import { deleteProjectId, getallProject, getDispatch, getTrack } from "../../../api/api";
import { ScaleLoader } from "react-spinners";
import DateFormet from "../../Dateformet/DateFormet";
import { MdClose, MdUpcoming, MdRefresh } from "react-icons/md";
import { FaCheckCircle, FaHourglassHalf, FaHourglassStart, FaFilePdf } from "react-icons/fa";
import { FiClock } from "react-icons/fi";
// import { AdjustmentsIcon } from "@heroicons/react/outline";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

/* ── Status config ── */
const STATUS_MAP = {
  upcoming: { label: "Upcoming", pill: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200", dot: "bg-indigo-500" },
  inprogress: { label: "In Progress", pill: "bg-amber-50 text-amber-700 ring-1 ring-amber-200", dot: "bg-amber-500" },
  completed: { label: "Completed", pill: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200", dot: "bg-emerald-500" },
  default: { label: "Unknown", pill: "bg-gray-100 text-gray-600 ring-1 ring-gray-200", dot: "bg-gray-400" },
};
const getStatus = (s = "") => STATUS_MAP[s.toLowerCase()] || STATUS_MAP.default;

/* ── Track dot ── */
const TrackDot = ({ status }) => {
  const icons = {
    completed: <CheckIcon className="w-3.5 h-3.5 text-emerald-600" />,
    inprogress: <ArrowPathIcon className="w-3.5 h-3.5 text-amber-500 animate-spin" />,
    upcoming: <MdUpcoming className="text-indigo-500 text-sm" />,
    "yet not start": <FiClock className="text-blue-400 text-sm" />,
    rejected: <XMarkIcon className="w-3.5 h-3.5 text-red-500" />,
    partiallycomplete: <AdjustmentsVerticalIcon className="w-3.5 h-3.5 text-orange-500" />,
  };
  return (
    <div className="w-7 h-7 rounded-full border-2 border-white shadow-sm bg-gray-50 flex items-center justify-center flex-shrink-0 z-10">
      {icons[status] || <FiClock className="text-gray-400 text-sm" />}
    </div>
  );
};

/* ══════════════════════════════════════════
   TRACKER SIDE PANEL
══════════════════════════════════════════ */
const TrackerPanel = ({ client, trackdata, onClose }) => (
  <AnimatePresence>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: "rgba(15,23,42,0.4)", backdropFilter: "blur(3px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md h-full bg-white shadow-2xl flex flex-col"
      >
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between">
          <div>
            <p className="text-[10px] font-bold tracking-widest text-blue-600 uppercase mb-1">
              Project Tracker
            </p>
            <h2 className="text-lg font-extrabold text-gray-900">{client.name}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{client.customerID?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {trackdata?.length > 0 ? (
            <div className="relative">
              <div className="absolute left-3 top-2 bottom-2 w-px bg-gradient-to-b from-blue-200 via-blue-100 to-transparent" />
              <div className="space-y-6">
                {trackdata.map((process, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.07 }}
                    className="flex gap-4 items-start"
                  >
                    <TrackDot status={process.status} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap mb-0.5">
                        <span className="font-semibold text-gray-800 text-sm">{process.process}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${getStatus(process.status).pill}`}>
                          {process.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider">{process.employee}</p>
                      <div className="flex flex-wrap gap-3 mt-1.5 text-[11px] text-gray-400">
                        <span className="flex items-center gap-1">
                          <UserIcon className="w-3 h-3" />
                          <DateFormet data={process.assignedAt} />
                        </span>
                        <span className="flex items-center gap-1">
                          <MdRefresh className="text-sm" />
                          <DateFormet data={process.updatedAt} />
                        </span>
                      </div>
                      {process.status === "completed" && process.bill?.billCopy && (
                        <button
                          onClick={() => window.open(process.bill?.billCopy, "_blank")}
                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full transition-colors"
                        >
                          <DocumentCheckIcon className="w-3.5 h-3.5" />
                          View Bill
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-52 gap-3">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <ArrowPathIcon className="w-5 h-5 text-gray-300" />
              </div>
              <p className="text-sm text-gray-400">No processes available</p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  </AnimatePresence>
);

/* ══════════════════════════════════════════
   DISPATCH SIDE PANEL
══════════════════════════════════════════ */
const DispatchPanel = ({ dispatchs, onClose }) => {
  const statusIcon = {
    "not yet start": <FaHourglassStart className="text-indigo-500" />,
    "in progress": <FaHourglassHalf className="text-amber-500" />,
    completed: <FaCheckCircle className="text-emerald-500" />,
  };
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex justify-end"
        style={{ background: "rgba(15,23,42,0.4)", backdropFilter: "blur(3px)" }}
        onClick={onClose}
      >
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-sm h-full bg-white shadow-2xl flex flex-col"
        >
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold tracking-widest text-emerald-600 uppercase mb-1">
                Dispatch Records
              </p>
              <h2 className="text-lg font-extrabold text-gray-900">
                {dispatchs?.length} Dispatch{dispatchs?.length !== 1 ? "es" : ""}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-xl border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {dispatchs?.length > 0 ? (
              <div className="space-y-3">
                {dispatchs.map((d, i) => (
                  <motion.div
                    key={d._id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }}
                    className="border border-gray-100 rounded-2xl p-4 bg-gray-50"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-bold text-gray-800 text-sm">Dispatch #{i + 1}</span>
                      <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 capitalize">
                        {d.status} {statusIcon[d.status]}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                      <div><p className="text-gray-400 mb-0.5">Quantity</p><p className="font-semibold text-gray-800">{d.quantity}</p></div>
                      {d?.startDate && <div><p className="text-gray-400 mb-0.5">Start</p><p className="font-semibold text-gray-800">{d.startDate.slice(0, 10)}</p></div>}
                      <div><p className="text-gray-400 mb-0.5">Dispatch Date</p><p className="font-semibold text-gray-800">{d?.dispatchDate?.slice(0, 10)}</p></div>
                    </div>
                    {d?.bill && (
                      <a
                        href={d.bill.billCopy}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-3 py-1 rounded-full transition-colors"
                      >
                        <DocumentCheckIcon className="w-3.5 h-3.5" />
                        View Bill
                      </a>
                    )}
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-40 text-sm text-gray-400">No dispatches available.</div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ══════════════════════════════════════════
   MOBILE PROJECT CARD
══════════════════════════════════════════ */
const MobileCard = ({ client, index, currentPage, rowsPerPage, onView, onEdit, onDelete, onTrack, onDispatch, navigate }) => {
  const sc = getStatus(client.status);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
    >
      {/* Colored top strip */}
      <div className={`h-1 ${client.status.toLowerCase() === "completed" ? "bg-emerald-400" : client.status.toLowerCase() === "inprogress" ? "bg-amber-400" : "bg-indigo-400"}`} />

      <div className="p-4">
        {/* Top */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className="text-[10px] font-bold text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
                #{(currentPage - 1) * rowsPerPage + index + 1}
              </span>
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${sc.pill}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                {client.status}
              </span>
            </div>
            <h3 className="font-extrabold text-gray-900 text-base leading-tight">{client.name}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{client.customerID?.name}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => navigate(`/invoice/${client._id}`)}
              className="w-8 h-8 rounded-xl border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-500 transition-colors"
              title="PDF"
            >
              <FaFilePdf className="text-xs" />
            </button>
            <button
              onClick={() => onEdit(client)}
              className="w-8 h-8 rounded-xl border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-500 transition-colors"
              title="Edit"
            >
              <PencilIcon className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDelete(client)}
              className="w-8 h-8 rounded-xl border border-red-100 hover:bg-red-50 flex items-center justify-center text-red-400 transition-colors"
              title="Delete"
            >
              <TrashIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Data grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 bg-gray-50 rounded-xl p-3 mb-3 text-xs">
          <div>
            <p className="text-gray-400 mb-0.5">Work Order</p>
            <p className="font-semibold text-gray-700">{client.workOrder}</p>
            {client.revision && (
              <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded mt-0.5 inline-block">
                {client.revision}
              </span>
            )}
          </div>
          <div>
            <p className="text-gray-400 mb-0.5">Part No</p>
            <p className="font-semibold text-gray-700">{client.partNo}</p>
          </div>
          <div>
            <p className="text-gray-400 mb-0.5">Quantity</p>
            <p className="font-bold text-gray-900">{client.quantity}</p>
          </div>
          <div>
            <p className="text-gray-400 mb-0.5">Value</p>
            <p className="font-bold text-emerald-600">₹{client.projectValue}</p>
          </div>
          <div className="col-span-2">
            <p className="text-gray-400 mb-0.5">Confirmation Date</p>
            <p className="font-semibold text-gray-700">{client.createdAt.slice(0, 10)}</p>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex gap-2">
          <button
            onClick={() => onView(client)}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-xl py-2.5 transition-colors"
          >
            <EyeIcon className="w-3.5 h-3.5" /> View Details
          </button>
          {client.dispatches.length > 0 ? (
            <button
              onClick={() => onDispatch(client._id)}
              className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 rounded-xl py-2.5 transition-colors"
            >
              ↗ Dispatches
            </button>
          ) : (
            <button
              onClick={() => onTrack(client._id)}
              className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-xl py-2.5 transition-colors"
            >
              Track →
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const ProjectTable = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [clients, setAddClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentProject, setCurrentProject] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [openTrackerIndex, setOpenTrackerIndex] = useState(null);
  const [trackdata, setTrackData] = useState([]);
  const [dispatchData, setDispatchData] = useState([]);
  const [openDispatch, setOpenDispatch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({});
  const rowsPerPage = 5;
  const navigate = useNavigate();
  const tabs = ["All", "Upcoming", "InProgress", "Completed"];

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const data = await getallProject(currentPage, rowsPerPage, debouncedSearch);
      setAddClients(data.data.projects);
      setTotalPages(data.data.totalPages || 1);
      setStats(data.data.stats || {});
    } catch { toast.error("Failed to load projects."); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCustomers(); }, [addProjectOpen, currentPage, debouncedSearch]);

  const filteredClients = activeTab === "All"
    ? clients
    : clients.filter((c) => c.status.toLowerCase() === activeTab.toLowerCase());

  const handleView = (p) => { setIsEdit(false); setIsView(true); setCurrentProject(p); setAddProjectOpen(true); };
  const handleEdit = (p) => { setIsEdit(true); setIsView(false); setCurrentProject(p); setAddProjectOpen(true); };
  const handleDelete = (p) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    deleteProjectId(p._id)
      .then((r) => { toast.success(r.message); fetchCustomers(); })
      .catch((e) => toast.error(e?.response?.data?.message || "Something went wrong"));
  };
  const handleTrackerToggle = (id) => getTrack(id).then((r) => { setTrackData(r.data); setOpenTrackerIndex(id); }).catch(console.error);
  const handleDispatchToggle = (id) => getDispatch(id).then((r) => { setDispatchData(r.data); setOpenDispatch(true); }).catch(console.error);

  const tabCounts = {
    All: stats?.upcoming + stats?.inprogress + stats?.completed + stats?.rejected || 0,
    Upcoming: stats?.upcoming || 0,
    InProgress: stats?.inprogress || 0,
    Completed: stats?.completed || 0,
  };

  if (addProjectOpen) {
    return (
      <AddProject
        onBack={() => { setAddProjectOpen(false); setCurrentProject(null); setIsView(false); setIsEdit(false); }}
        currentProject={currentProject}
        isEdit={isEdit}
        isView={isView}
      />
    );
  }

  const paginationItems = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
    .reduce((acc, p, i, arr) => {
      if (i > 0 && p - arr[i - 1] > 1) acc.push("e-" + p);
      acc.push(p);
      return acc;
    }, []);

  return (
    <>
      <div className="min-h-screen bg-slate-50 p-3 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">

          {/* ── Page header ── */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight leading-none">
                Projects
              </h1>
              <p className="text-sm text-gray-400 mt-1.5">
                {clients.length} total project{clients.length !== 1 ? "s" : ""}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2.5 shadow-sm flex-1 sm:flex-none sm:w-56 focus-within:ring-2 focus-within:ring-gray-900/10 focus-within:border-gray-400 transition-all">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search projects…"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="text-sm text-gray-700 placeholder-gray-400 outline-none bg-transparent w-full"
                />
              </div>
              <button
                onClick={() => { setCurrentProject(null); setAddProjectOpen(true); }}
                className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 active:scale-95 text-white text-sm font-bold px-4 py-2.5 rounded-xl shadow-sm transition-all whitespace-nowrap"
              >
                <PlusIcon className="w-4 h-4" />
                Add Project
              </button>
            </div>
          </div>

          {/* ── Main card ── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

            {/* ── Tabs ── */}
            <div className="flex overflow-x-auto border-b border-gray-100 scrollbar-none">
              {tabs.map((tab) => {
                const active = activeTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex items-center gap-2 px-4 sm:px-5 py-4 text-xs sm:text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${active
                      ? "border-gray-900 text-gray-900"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    {tab}
                    <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center ${active ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-500"
                      }`}>
                      {tabCounts[tab]}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* ── Loading ── */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <ScaleLoader color="#111827" />
              </div>

              /* ── Empty ── */
            ) : filteredClients.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 gap-3">
                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center">
                  <MagnifyingGlassIcon className="w-6 h-6 text-gray-300" />
                </div>
                <p className="text-sm text-gray-400 font-medium">No projects found</p>
                <p className="text-xs text-gray-300">Try adjusting your search or filters</p>
              </div>

            ) : (
              <>
                {/* ════════════════════════════
                    DESKTOP TABLE (md+)
                ════════════════════════════ */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-gray-100">
                        {["#", "Project / Customer", "Conf. Date", "Work Order", "Part No", "Qty / Value", "Status", "Actions"].map((h) => (
                          <th key={h} className="px-5 py-3.5 text-left text-[10px] font-bold tracking-widest text-gray-400 uppercase whitespace-nowrap first:pl-5 last:pr-5">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredClients.map((client, index) => {
                        const sc = getStatus(client.status);
                        return (
                          <motion.tr
                            key={client._id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: index * 0.04 }}
                            className="hover:bg-slate-50/80 transition-colors group"
                          >
                            {/* # */}
                            <td className="px-5 py-4 w-10">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-gray-100 text-[10px] font-bold text-gray-500">
                                {(currentPage - 1) * rowsPerPage + index + 1}
                              </span>
                            </td>

                            {/* Project */}
                            <td className="px-5 py-4 max-w-[200px]">
                              <p className="font-semibold text-gray-900 truncate text-[13px]">{client.name}</p>
                              <p className="text-xs text-gray-400 mt-0.5 truncate">{client.customerID?.name}</p>
                            </td>

                            {/* Date */}
                            <td className="px-5 py-4 text-xs text-gray-500 whitespace-nowrap">
                              {client.createdAt.slice(0, 10)}
                            </td>

                            {/* Work Order */}
                            <td className="px-5 py-4">
                              <p className=" text-xs text-gray-700 font-semibold">{client.workOrder}</p>
                              {client.revision && (
                                <span className="mt-1 inline-block text-[10px] font-bold text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded-md">
                                  {client.revision}
                                </span>
                              )}
                            </td>

                            {/* Part No */}
                            <td className="px-5 py-4">
                              <p className=" text-xs text-gray-700 font-semibold">{client.partNo}</p>
                              {client.dispatches.length > 0 && (
                                <button
                                  onClick={() => handleDispatchToggle(client._id)}
                                  className="mt-1 text-[10px] font-bold text-emerald-600 hover:text-emerald-700 uppercase tracking-wide transition-colors"
                                >
                                  ↗ Dispatches
                                </button>
                              )}
                            </td>

                            {/* Qty / Value */}
                            <td className="px-5 py-4 whitespace-nowrap">
                              <p className="font-bold text-gray-900 text-sm">{client.quantity}</p>
                              <p className="text-xs font-semibold text-emerald-600 mt-0.5">₹{client.projectValue}</p>
                            </td>

                            {/* Status */}
                            <td className="px-5 py-4 whitespace-nowrap">
                              <div className="flex flex-col items-start gap-1.5">
                                <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${sc.pill}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                                  {client.status}
                                </span>
                                {client.dispatches.length === 0 && (
                                  <button
                                    onClick={() => handleTrackerToggle(client._id)}
                                    className="text-[10px] font-bold text-blue-600 hover:text-blue-700 uppercase tracking-wide transition-colors"
                                  >
                                    Track →
                                  </button>
                                )}
                              </div>
                            </td>

                            {/* Actions */}
                            <td className="px-5 py-4 pr-5">
                              <div className="flex items-center gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => navigate(`/invoice/${client._id}`)}
                                  className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-gray-100 flex items-center justify-center text-gray-500 transition-colors"
                                  title="PDF"
                                >
                                  <FaFilePdf className="text-xs" />
                                </button>
                                <button
                                  onClick={() => handleView(client)}
                                  className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-gray-100 flex items-center justify-center text-gray-500 transition-colors"
                                  title="View"
                                >
                                  <EyeIcon className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleEdit(client)}
                                  className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-gray-100 flex items-center justify-center text-gray-500 transition-colors"
                                  title="Edit"
                                >
                                  <PencilIcon className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDelete(client)}
                                  className="w-8 h-8 rounded-lg border border-red-100 hover:bg-red-50 flex items-center justify-center text-red-400 hover:text-red-600 transition-colors"
                                  title="Delete"
                                >
                                  <TrashIcon className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ════════════════════════════
                    MOBILE CARDS (< md)
                ════════════════════════════ */}
                <div className="md:hidden p-3 space-y-3">
                  {filteredClients.map((client, index) => (
                    <MobileCard
                      key={client._id}
                      client={client}
                      index={index}
                      currentPage={currentPage}
                      rowsPerPage={rowsPerPage}
                      onView={handleView}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onTrack={handleTrackerToggle}
                      onDispatch={handleDispatchToggle}
                      navigate={navigate}
                    />
                  ))}
                </div>
              </>
            )}

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                <p className="text-xs text-gray-400 hidden sm:block">
                  Page <span className="font-semibold text-gray-700">{currentPage}</span> of{" "}
                  <span className="font-semibold text-gray-700">{totalPages}</span>
                </p>
                <div className="flex items-center gap-1.5 mx-auto sm:mx-0">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-gray-600 transition-colors shadow-sm"
                  >
                    <ChevronLeftIcon className="w-3.5 h-3.5" />
                  </button>

                  {paginationItems.map((item) =>
                    typeof item === "string" ? (
                      <span key={item} className="text-gray-400 text-xs px-0.5">···</span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setCurrentPage(item)}
                        className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all shadow-sm ${currentPage === item
                          ? "bg-gray-900 text-white border border-gray-900"
                          : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                          }`}
                      >
                        {item}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-gray-600 transition-colors shadow-sm"
                  >
                    <ChevronRightIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Side Panels ── */}
      {openTrackerIndex && (
        <TrackerPanel
          client={clients.find((c) => c._id === openTrackerIndex) || { name: "", customerID: {} }}
          trackdata={trackdata}
          onClose={() => setOpenTrackerIndex(null)}
        />
      )}
      {openDispatch && (
        <DispatchPanel
          dispatchs={dispatchData}
          onClose={() => setOpenDispatch(false)}
        />
      )}
    </>
  );
};

export default ProjectTable;